/**
 * Papergen API client — typed CRUD + generation + render calls.
 *
 * Uses `useAuthenticatedRequest` for race-condition-free auth gating.
 * All calls return raw Axios promises; error handling is at the component level.
 */
import axios from 'axios';
import {
  Workspace, PaperNode, PaperNodeVersion,
  GroundingResult, GenerateResult,
  StyleProfile, PaperTemplate,
  TreeNode, MarkGrid,
} from '../types/papergen';

const BASE = '/api/papergen';

// ──────────────────────────────────────────────────
// Workspaces + tree
// ──────────────────────────────────────────────────

export const listWorkspaces = (token: string) =>
  axios.get(`${BASE}/workspaces/`, { headers: { Authorization: `Token ${token}` } });

export const getWorkspaceTree = (workspaceId: number, token: string) =>
  axios.get<{ nodes: TreeNode[]; mark_grid: MarkGrid }>(`${BASE}/workspaces/${workspaceId}/tree/`, {
    headers: { Authorization: `Token ${token}` },
  });

// ──────────────────────────────────────────────────
// Nodes CRUD
// ──────────────────────────────────────────────────

export const listPaperNodes = (workspaceId: number, token: string) =>
  axios.get(`${BASE}/workspaces/${workspaceId}/nodes/`, {
    headers: { Authorization: `Token ${token}` },
  });

export const createPaperNode = (workspaceId: number, data: any, token: string) =>
  axios.post<PaperNode>(`${BASE}/workspaces/${workspaceId}/nodes/`, data, {
    headers: { Authorization: `Token ${token}` },
  });

export const updatePaperNode = (nodeId: number, data: any, token: string) =>
  axios.patch<PaperNode>(`${BASE}/nodes/${nodeId}/`, data, {
    headers: { Authorization: `Token ${token}` },
  });

export const deletePaperNode = (nodeId: number, token: string) =>
  axios.delete(`${BASE}/nodes/${nodeId}/`, {
    headers: { Authorization: `Token ${token}` },
  });

export const reorderPaperNode = (
  nodeId: number,
  params: { new_ordinal: number; new_parent_id?: number | null },
  token: string,
) =>
  axios.post(`${BASE}/nodes/${nodeId}/reorder/`, params, {
    headers: { Authorization: `Token ${token}` },
  });

// ──────────────────────────────────────────────────
// Generation
// ──────────────────────────────────────────────────

export const groundWiki = (data: { grade?: number; topic_num?: string; status?: string }, token: string) =>
  axios.post<GroundingResult>(`${BASE}/grounding/`, data, {
    headers: { Authorization: `Token ${token}` },
  });

export const generateNodes = (workspaceId: number, spec: any, token: string) =>
  axios.post<GenerateResult>(`${BASE}/generate/`, { workspace_id: workspaceId, spec }, {
    headers: { Authorization: `Token ${token}` },
  });

export const regenerateNode = (nodeId: number, token: string) =>
  axios.post(`${BASE}/nodes/${nodeId}/regenerate/`, {}, {
    headers: { Authorization: `Token ${token}` },
  });

export const regenerateSubtree = (nodeId: number, token: string) =>
  axios.post(`${BASE}/nodes/${nodeId}/regenerate-subtree/`, {}, {
    headers: { Authorization: `Token ${token}` },
  });

// ──────────────────────────────────────────────────
// Versions
// ──────────────────────────────────────────────────

export const listNodeVersions = (nodeId: number, token: string) =>
  axios.get<PaperNodeVersion[]>(`${BASE}/nodes/${nodeId}/versions/`, {
    headers: { Authorization: `Token ${token}` },
  });

export const restoreNodeVersion = (nodeId: number, versionId: number, token: string) =>
  axios.post(`${BASE}/nodes/${nodeId}/versions/${versionId}/restore/`, {}, {
    headers: { Authorization: `Token ${token}` },
  });

// ──────────────────────────────────────────────────
// Rendering
// ──────────────────────────────────────────────────

export const renderDocx = (workspaceId: number, markingCopy: boolean, token: string) =>
  axios.post(`${BASE}/workspaces/${workspaceId}/render/docx/`, { marking_copy: markingCopy }, {
    headers: { Authorization: `Token ${token}` },
    responseType: 'blob',
  });

export const renderLatex = (workspaceId: number, token: string) =>
  axios.post<{ tex: string; workspace_id: number }>(`${BASE}/workspaces/${workspaceId}/render/latex/`, {}, {
    headers: { Authorization: `Token ${token}` },
  });

export const renderLatexPdf = (workspaceId: number, token: string) =>
  axios.post(`${BASE}/workspaces/${workspaceId}/render/latex-pdf/`, {}, {
    headers: { Authorization: `Token ${token}` },
    responseType: 'blob',
  });

// ──────────────────────────────────────────────────
// StyleProfiles + Templates (Step 10)
// ──────────────────────────────────────────────────

export const listStyleProfiles = (token: string) =>
  axios.get<StyleProfile[]>(`${BASE}/style-profiles/`, {
    headers: { Authorization: `Token ${token}` },
  });

export const createStyleProfile = (data: any, token: string) =>
  axios.post<StyleProfile>(`${BASE}/style-profiles/create/`, data, {
    headers: { Authorization: `Token ${token}` },
  });

export const getStyleProfile = (profileId: number, token: string) =>
  axios.get<StyleProfile>(`${BASE}/style-profiles/${profileId}/`, {
    headers: { Authorization: `Token ${token}` },
  });

export const updateStyleProfile = (profileId: number, data: any, token: string) =>
  axios.patch<StyleProfile>(`${BASE}/style-profiles/${profileId}/`, data, {
    headers: { Authorization: `Token ${token}` },
  });

export const deleteStyleProfile = (profileId: number, token: string) =>
  axios.delete(`${BASE}/style-profiles/${profileId}/`, {
    headers: { Authorization: `Token ${token}` },
  });

export const listTemplates = (token: string) =>
  axios.get<PaperTemplate[]>(`${BASE}/templates/`, {
    headers: { Authorization: `Token ${token}` },
  });

export const saveAsTemplate = (workspaceId: number, data: { name: string; scope?: string }, token: string) =>
  axios.post<PaperTemplate>(`${BASE}/workspaces/${workspaceId}/save-as-template/`, data, {
    headers: { Authorization: `Token ${token}` },
  });

export const getTemplate = (templateId: number, token: string) =>
  axios.get<PaperTemplate>(`${BASE}/templates/${templateId}/`, {
    headers: { Authorization: `Token ${token}` },
  });

export const updateTemplate = (templateId: number, data: any, token: string) =>
  axios.patch<PaperTemplate>(`${BASE}/templates/${templateId}/`, data, {
    headers: { Authorization: `Token ${token}` },
  });

export const deleteTemplate = (templateId: number, token: string) =>
  axios.delete(`${BASE}/templates/${templateId}/`, {
    headers: { Authorization: `Token ${token}` },
  });

export const instantiateTemplate = (templateId: number, data: any, token: string) =>
  axios.post(`${BASE}/templates/${templateId}/instantiate/`, data, {
    headers: { Authorization: `Token ${token}` },
  });
